import React from "react";
import { AuthenticatedTemplate, UnauthenticatedTemplate, useMsal } from "@azure/msal-react";
import {
  AppBar,
  Box,
  Button,
  CssBaseline,
  Divider,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Toolbar,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import MenuIcon from "@mui/icons-material/Menu";
import LogoutIcon from "@mui/icons-material/Logout";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import IconButton from "@mui/material/IconButton";
import JumboDdPopover from "@jumbo/components/JumboDdPopover";
import AppRoutes from "./AppRoutes";
import AuthLanding from "./auth/AuthLanding";
import NavMenu, { drawerWidth } from "./NavMenu";
import PortalProviders from "./PortalProviders";
import { getUsersAccess } from "@portal/utils/roleFunctions";
import { config as portalConfig } from "@portal/config/main";
import { SIDEBAR_ANCHOR_POSITIONS, SIDEBAR_STYLES, SIDEBAR_VARIANTS, SIDEBAR_VIEWS } from "@jumbo/utils/constants/layout";

const miniWidth = portalConfig?.defaultContentLayout?.sidebar?.minWidth || 80;

const AccountDropdown = ({ account, onLogout }) => {
  return (
    <JumboDdPopover
      triggerButton={
        <IconButton
          color="inherit"
          size="small"
          sx={{
            border: 1,
            borderColor: "divider",
            bgcolor: "background.paper",
            pointerEvents: "auto",
          }}
        >
          <PersonOutlineIcon fontSize="small" />
        </IconButton>
      }
    >
      <Box sx={{ minWidth: 240 }}>
        <Box sx={{ px: 2, py: 1.5 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            {account?.name || account?.username || "Account"}
          </Typography>
          {account?.username ? (
            <Typography variant="body2" color="text.secondary">
              {account.username}
            </Typography>
          ) : null}
        </Box>
        <Divider />
        <List disablePadding sx={{ py: 1 }}>
          <ListItemButton onClick={onLogout}>
            <ListItemIcon sx={{ minWidth: 36 }}>
              <LogoutIcon />
            </ListItemIcon>
            <ListItemText primary="Logout" sx={{ my: 0 }} />
          </ListItemButton>
        </List>
      </Box>
    </JumboDdPopover>
  );
};

const Shell = () => {
  const { instance, accounts } = useMsal();
  const theme = useTheme();
  const upLg = useMediaQuery("(min-width:1200px)");
  const account = accounts?.[0];
  const access = React.useMemo(() => getUsersAccess(account), [account]);
  const companyId = React.useMemo(() => {
    const claims = account?.idTokenClaims || {};
    const id =
      claims.extension_CompanyId ??
      claims.extension_companyId ??
      claims.companyId ??
      claims.companyID ??
      claims.companyid;
    if (id === undefined || id === null) {
      return null;
    }
    const value = `${id}`.trim();
    return value.length ? value : null;
  }, [account]);
  const headerBackground = theme.components?.MuiAppBar?.styleOverrides?.root?.background;
  const headerColor = theme.components?.MuiAppBar?.styleOverrides?.root?.color;

  const [sidebarOptions, setSidebarOptions] = React.useState({
    view: SIDEBAR_VIEWS.FULL,
    open: true,
    width: drawerWidth,
    minWidth: miniWidth,
    variant: SIDEBAR_VARIANTS.PERSISTENT,
    style: SIDEBAR_STYLES.FULL_HEIGHT,
    anchor: SIDEBAR_ANCHOR_POSITIONS.LEFT,
    hide: false,
  });

  React.useEffect(() => {
    if (upLg) {
      setSidebarOptions((prev) => ({
        ...prev,
        variant: SIDEBAR_VARIANTS.PERSISTENT,
        open: prev.view === SIDEBAR_VIEWS.MINI ? false : true,
      }));
    } else {
      setSidebarOptions((prev) => ({
        ...prev,
        variant: SIDEBAR_VARIANTS.TEMPORARY,
        view: SIDEBAR_VIEWS.FULL,
        open: false,
      }));
    }
  }, [upLg]);

  const sidebarOffset = React.useMemo(() => {
    if (sidebarOptions.variant === SIDEBAR_VARIANTS.TEMPORARY) {
      return 0;
    }
    const fullWidth = sidebarOptions.width || drawerWidth;
    const mini = sidebarOptions.minWidth || miniWidth;
    if (sidebarOptions.view === SIDEBAR_VIEWS.MINI) {
      return sidebarOptions.open ? fullWidth : mini;
    }
    return sidebarOptions.open ? fullWidth : 0;
  }, [sidebarOptions]);

  const mainWidth = React.useMemo(() => {
    if (sidebarOptions.variant === SIDEBAR_VARIANTS.TEMPORARY) {
      return "100%";
    }
    return `calc(100% - ${sidebarOffset}px)`;
  }, [sidebarOffset, sidebarOptions.variant]);

  const logout = React.useCallback(() => {
    instance.logoutRedirect();
  }, [instance]);

  if (!companyId) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: theme.palette.background.default,
          px: 3,
        }}
      >
        <Paper
          elevation={3}
          sx={{
            p: { xs: 3, sm: 4 },
            maxWidth: 520,
            width: "100%",
            textAlign: "center",
            borderRadius: 3,
            bgcolor: theme.palette.background.paper,
          }}
        >
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 1, color: theme.palette.primary.main }}>
            Welcome to OysterMate
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Your account hasn&apos;t been set up yet. Please contact <strong>itadmin@aoc.group</strong> to get you started.
          </Typography>
          <Button variant="contained" color="primary" onClick={logout}>
            Sign out
          </Button>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: "100vh", display: "flex", bgcolor: theme.palette.background.default }}>
      <AppBar
        position="fixed"
        color="default"
        elevation={0}
        sx={{
          borderBottom: 1,
          borderColor: theme.palette.divider,
          background: headerBackground || theme.palette.background.paper,
          color: headerColor || theme.palette.text.primary,
        }}
      >
        <Toolbar sx={{ minHeight: 80 }}>
            <IconButton
              edge="start"
              color="inherit"
              onClick={() =>
                setSidebarOptions((prev) => {
                  return{
                  ...prev,
                  open: !prev.open,
                    view: prev.view === SIDEBAR_VIEWS.MINI ? SIDEBAR_VIEWS.FULL : prev.view,
                    variant: upLg ? SIDEBAR_VARIANTS.PERSISTENT :  SIDEBAR_VARIANTS.TEMPORARY ,
                }})
              }
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>
          <Typography variant="h6" sx={{ flexGrow: 1, fontWeight: 700 }} color={theme.palette.primary.main}>
            OysterMate
          </Typography>
          <Stack direction="row" spacing={2} alignItems="center">
           
            <AccountDropdown account={account} onLogout={logout} />
          </Stack>
        </Toolbar>
      </AppBar>
      <NavMenu sidebarOptions={sidebarOptions} setSidebarOptions={setSidebarOptions} />
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          ml: `${sidebarOffset}px`,
          width: mainWidth,
          px: 4,
          py: 5,
          mt: 12,
          bgcolor: theme.palette.background.default,
          transition: theme.transitions.create(["margin-left", "width"], {
            duration: theme.transitions.duration.shortest,
            easing: theme.transitions.easing.sharp,
          }),
        }}
      >
        <AppRoutes access={access} />
      </Box>
    </Box>
  );
};

const App = () => (
  <PortalProviders>
    <CssBaseline />
    <AuthenticatedTemplate>
      <Shell />
    </AuthenticatedTemplate>
    <UnauthenticatedTemplate>
      <AuthLanding />
    </UnauthenticatedTemplate>
  </PortalProviders>
);

export default App;
